import styled from 'styled-components';

export const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(6)};
`;

export const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 17px;
`;

export const Panel = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

export const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

export const Small = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
`;

/** Текст ошибки — `role="alert"`, чтобы экранный диктор прочёл его сразу. */
export const ErrorText = styled.div.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

/** Кнопка в виде ссылки («ещё N», «повторить»). */
export const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

/** Плашка-сообщение: итог прошлой попытки, ошибка обновления, замечания загрузки. */
export const Notice = styled.div<{ $tone: 'warn' | 'bad' | 'info' }>`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid
    ${({ $tone, theme }) =>
      $tone === 'bad'
        ? theme.colors.status.danger
        : $tone === 'warn'
          ? theme.colors.status.warning
          : theme.colors.status.info};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  font-size: 14px;
`;

export const NoticeTitle = styled.div`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

/** Сворачиваемый список (листы прежней ревизии, ревизии итога). */
export const Details = styled.details`
  margin-top: ${({ theme }) => theme.spacing(2)};

  summary {
    cursor: pointer;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Строка под комплектом: перенос листов прежней ревизии. */
export const SubCell = styled.td`
  padding-top: 0;
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  flex-wrap: wrap;
`;

export const Spread = styled(Row)`
  justify-content: space-between;
`;

export const Drop = styled.div<{ $active: boolean }>`
  border: 2px dashed
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing(8)} ${({ theme }) => theme.spacing(5)};
  text-align: center;
  transition: background ${({ theme }) => theme.motion.fast};
`;

export const DropTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const HiddenInput = styled.input`
  display: none;
`;

export const List = styled.ul`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Tone = styled.span<{ $tone: 'ok' | 'warn' | 'bad' | 'muted' | 'accent' }>`
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $tone, theme }) =>
    $tone === 'ok'
      ? theme.colors.status.success
      : $tone === 'warn'
        ? theme.colors.status.warning
        : $tone === 'bad'
          ? theme.colors.status.danger
          : $tone === 'accent'
            ? theme.colors.primary
            : theme.colors.text.secondary};
`;

export const Mine = styled.tr<{ $mine: boolean }>`
  td {
    font-weight: ${({ $mine }) => ($mine ? 600 : 400)};
  }
`;

export const Cell = styled.td`
  vertical-align: top;
`;

export const NarrowInput = styled.input`
  width: 72px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
`;

export const WideInput = styled(NarrowInput)`
  width: 100%;
  min-width: 220px;
`;

export const Select = styled.select`
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  max-width: 100%;
`;

export const Code = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  word-break: break-all;
`;

export const TitulCard = styled(Panel)`
  margin-top: ${({ theme }) => theme.spacing(4)};
  overflow-x: auto;
`;

export const TitulHead = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 420px);
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const TargetBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
`;

export const StickyBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin-top: ${({ theme }) => theme.spacing(5)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  box-shadow: ${({ theme }) => theme.shadow.md};
`;

export const CenterRow = styled(Row)`
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;
